/**
 * @file index.jsx
 * @author Rishikesh
 * @date 2024-09-11
 * @description Manages the leads informations and listings
 */
import React, { useCallback, useState } from "react";
import { useSelector } from "react-redux";
import { Box } from "@mui/material";
import { useFetchData } from "../../components/hooks/useFetchData";
import { LeadsList } from "./LeadsList";

const PAGE_SIZE = 25;

const LeadsPage = () => {
  const [minLoading, setMinLoading] = useState(true);
  const [listLoading, setListLoading] = useState(false);
  const [leadList, setLeadList] = useState([]);
  const [userList, setUserList] = useState([]);
  const [page, setPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [assigneeFilter, setAssigneeFilter] = useState("");
  const [isdeletedFilter, setIsdeletedFilter] = useState("0");

  const role = useSelector((state) => state.auth.user?.role);
  const isAdmin = role !== "employee" && role !== "agent";

  const { fetchData } = useFetchData();

  const fetchLeads = useCallback(
    async ({
      nextPage = page,
      nextSearch = searchQuery,
      nextAssignee = assigneeFilter,
      nextIsdeleted = isdeletedFilter,
    } = {}) => {
      const params = new URLSearchParams({
        type: "lead",
        page: String(nextPage + 1),
        limit: String(PAGE_SIZE),
        isdeleted: nextIsdeleted,
      });

      if (nextSearch.trim()) {
        params.set("search", nextSearch.trim());
      }

      if (isAdmin && nextAssignee) {
        params.set("ownerid", nextAssignee);
      }

      setListLoading(true);
      const result = await fetchData(`getcandidates?${params.toString()}`);
      setListLoading(false);

      if (result?.code === 200) {
        const payload = result.data;
        if (Array.isArray(payload)) {
          setLeadList(payload);
          setTotalCount(payload.length);
        } else {
          setLeadList(payload?.candidates || []);
          setTotalCount(payload?.totalCount || 0);
        }
      }
    },
    [assigneeFilter, fetchData, isAdmin, isdeletedFilter, page, searchQuery]
  );

  const getAllUsers = async () => {
    const result = await fetchData("getusers");
    if (result?.code === 200) {
      setUserList(result.data);
    }
  };

  React.useEffect(() => {
    if (isAdmin) {
      getAllUsers();
    }
    const minLoadingTimer = setTimeout(() => {
      setMinLoading(false);
    }, 1500);

    return () => clearTimeout(minLoadingTimer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  React.useEffect(() => {
    const debounceMs = searchQuery.trim() ? 400 : 0;
    const timer = setTimeout(() => {
      fetchLeads({
        nextPage: page,
        nextSearch: searchQuery,
        nextAssignee: assigneeFilter,
        nextIsdeleted: isdeletedFilter,
      });
    }, debounceMs);

    return () => clearTimeout(timer);
  }, [assigneeFilter, fetchLeads, isdeletedFilter, page, searchQuery]);

  const handleSearchChange = (value) => {
    setSearchQuery(value);
    setPage(0);
  };

  const handleAssigneeFilterChange = (value) => {
    setAssigneeFilter(value);
    setPage(0);
  };

  const handleIsdeletedFilterChange = (value) => {
    setIsdeletedFilter(value);
    setPage(0);
  };

  const handlePageChange = (nextPage) => {
    setPage(nextPage);
  };

  const isInitialLoading =
    leadList.length === 0 && (listLoading || minLoading);

  const isTableLoading = listLoading && leadList.length > 0;

  return (
    <>
      <Box
        display="flex"
        flexDirection="column"
        width="100%"
        height="100%"
        overflow="hidden"
      >
        <LeadsList
          leads={leadList}
          userList={userList}
          isLoading={isInitialLoading}
          isTableLoading={isTableLoading}
          page={page}
          pageSize={PAGE_SIZE}
          totalCount={totalCount}
          searchQuery={searchQuery}
          assigneeFilter={assigneeFilter}
          isdeletedFilter={isdeletedFilter}
          onSearchChange={handleSearchChange}
          onAssigneeFilterChange={handleAssigneeFilterChange}
          onIsdeletedFilterChange={handleIsdeletedFilterChange}
          onPageChange={handlePageChange}
        />
      </Box>
    </>
  );
};

export default LeadsPage;
